type JsonArray = Readonly<Record<string, unknown>> | readonly unknown[];

function arrayLike(value: unknown): value is JsonArray {
    return value !== null && typeof value === 'object';
}
function own(value: unknown, key: string): unknown {
    return arrayLike(value) && Object.prototype.hasOwnProperty.call(value, key)
        ? (value as Readonly<Record<string, unknown>>)[key] : undefined;
}
function clone(value: unknown): unknown {
    if (!arrayLike(value)) return value;
    const entries = Object.entries(value);
    // PHP decodes JSON objects as associative arrays. Empty and sequential numeric
    // maps serialize as lists again, including nested maps in a returned field.
    return entries.every(([key], index) => key === String(index))
        ? entries.map(([, entry]) => clone(entry))
        : Object.fromEntries(entries.map(([key, entry]) => [key, clone(entry)]));
}
function collapse(value: unknown): unknown {
    return arrayLike(value) && Object.prototype.hasOwnProperty.call(value, '0') ? own(value, '0') : value;
}

/** Physical inventory collections retain their entries when PHP encodes sparse indices as an object. */
export function inventoryRowList(value: unknown): Record<string, unknown>[] {
    return arrayLike(value) ? Object.values(value).filter((node): node is Record<string, unknown> =>
        arrayLike(node) && !Array.isArray(node)) : [];
}
export function readInventoryRows(row: JsonArray | null, key: 'inventory' | 'inventory_in_container' | 'encounter_inventories_arr'): Record<string, unknown>[] {
    return inventoryRowList(own(row, key));
}

/** Canonical inventory_field read; absent defaults require a nonempty string instance ID. */
export function readInventoryField(row: JsonArray | null, key: string): unknown {
    const bucket = own(row, 'inventory');
    if (arrayLike(bucket) && Object.prototype.hasOwnProperty.call(bucket, key)) return clone(collapse(own(bucket, key)) ?? null);
    const inventoryId = collapse(own(bucket, 'inventory_id'));
    if (typeof inventoryId === 'string' && inventoryId !== '') {
        if (key === 'item_qty') return 1;
        if (key === 'inventory_thumbnail_pic_id') return clone(collapse(own(bucket, 'inventory_presentation_pic_id')) ?? null);
    }
    return null;
}

const trimPhp = (value: string): string => value.replace(/^[\x00\x09\x0A\x0B\x0D\x20]+|[\x00\x09\x0A\x0B\x0D\x20]+(?![\s\S])/g, '');
// PHP's PCRE $ accepts one final LF. Rich-shell fallback returns that raw value,
// whereas the direct/scoring-token paths trim PHP's ASCII whitespace first.
const validUuid = (value: string): boolean => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(?:\n)?(?![\s\S])/i.test(value);

/** Raw layer_list_entry_uuid extraction; deliberately does not validate or trim. */
export function readLayerListEntryUuid(cell: unknown): string {
    if (typeof cell === 'string') return cell.split('|', 1)[0]!;
    if (!arrayLike(cell)) return '';
    for (const value of [own(cell, 'item_layer_effect'), own(own(cell, 'items'), 'item_id'), own(cell, 'item_id')]) {
        if (typeof value === 'string' && value !== '') return value.split('|', 1)[0]!;
    }
    return '';
}

/** Exact item_arr_uuid_slot selection for JSON values, including effect-before-shell precedence. */
export function readItemUuidSlot(cell: unknown): string {
    if (typeof cell === 'string') {
        const value = trimPhp(cell);
        return validUuid(value) ? value : '';
    }
    if (!arrayLike(cell)) return '';
    // Non-string scoring tokens (numbers, booleans and PHP's "Array") cannot
    // satisfy the UUID pattern, so only a string token can return on this path.
    const token = Object.prototype.hasOwnProperty.call(cell, 'item_layer_effect')
        ? own(cell, 'item_layer_effect') : own(cell, '0');
    if (typeof token === 'string') {
        const value = trimPhp(token);
        if (validUuid(value)) return value;
    }
    const value = readLayerListEntryUuid(cell);
    return validUuid(value) ? value : '';
}

/** With a creature context, only its directly possessed, live, unabsorbed gear is
 * equipped. A contained row's stale slot is provenance, never hand occupancy.
 * Context-free reads retain the persisted token for save/preimage comparisons. */
export function readInventoryEquippedToken(row: JsonArray | null, holder?: JsonArray): string {
    if (holder) {
        if (Number(readInventoryField(row, 'inventory_private')) === 2 || Number(readInventoryField(row, 'inventory_deleted')) > 0) return '';
        const parent = String(readInventoryField(row, 'possess_id') ?? '').toUpperCase();
        const owners = [own(own(holder, 'encounter_creatures'), 'encounter_creature_id'),
            own(own(holder, 'player_characters'), 'item_id'), own(own(holder, 'items'), 'item_id')]
            .map(value => String(collapse(value) ?? '').toUpperCase()).filter(Boolean);
        // Older compile fixtures omit possess_id; their direct-list membership
        // supplies ownership. An explicit different parent always takes precedence.
        if (parent && owners.length && !owners.includes(parent)) return '';
    }
    return readItemUuidSlot(readInventoryField(row, 'equipped'));
}
